var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { TradeAssetsPageSchema } from '@shared/zod-schemas/frontend';
import { convertBasicTokenInfoToCovalentBalanceItem } from '@/hooks/pricing/useBasicTokenInfo';
import { useSwap } from '@/contexts/SwapProvider';
import { useSession } from '@/contexts/LitSessionProvider';
import { useOrderHistory } from '@/hooks/db/useOrderHistory';
import { useSearchParams } from 'next/navigation';
import useERC20Balance from '@/hooks/portfolio/useERC20Balance';
import { useSearchHome } from '@/contexts/SearchHomeProvider';
import { BuySellAssetControllerStages } from '@shared/types/frontend/assetPage';
import { useTokenData } from '@/hooks/asset/useTokenData';
export var useTradeForm = function () {
    var PKP = useSession().PKP;
    var _a = useSearchHome(), setKeyword = _a.setKeyword, setMode = _a.setMode;
    var urlParams = useSearchParams();
    var _b = [
        urlParams.get('address'),
        urlParams.get('networkId'),
        urlParams.get('side'),
    ], tokenAddress = _b[0], tokenNetwork = _b[1], tokenSide = _b[2];
    var _c = useState(BuySellAssetControllerStages.default), stage = _c[0], setStage = _c[1];
    var tokenData = useTokenData({ tokenAddress: tokenAddress, tokenNetwork: tokenNetwork }).data;
    var assetData = useERC20Balance({
        evmAddresses: [PKP === null || PKP === void 0 ? void 0 : PKP.additionalEthAddress],
        solanaAddresses: [PKP === null || PKP === void 0 ? void 0 : PKP.solanaAddress],
        useGeniusAPI: true,
    }).data;
    var refetchOrderHistory = useOrderHistory({
        cloudWallet: PKP === null || PKP === void 0 ? void 0 : PKP.additionalEthAddress,
        solanaAddress: PKP === null || PKP === void 0 ? void 0 : PKP.solanaAddress,
    }).refetch;
    var _d = useSwap(), setFromAssets = _d.setFromAssets, setToAssets = _d.setToAssets, toAssets = _d.toAssets;
    var _convertTokenData = function () {
        var _a;
        var _params = {
            token: tokenData === null || tokenData === void 0 ? void 0 : tokenData.token,
            tokenInfo: (_a = tokenData === null || tokenData === void 0 ? void 0 : tokenData.tokenInfo) === null || _a === void 0 ? void 0 : _a.results,
            prices: [tokenData === null || tokenData === void 0 ? void 0 : tokenData.prices],
        };
        var _convertedAsset = convertBasicTokenInfoToCovalentBalanceItem(_params);
        return _convertedAsset;
    };
    var form = useForm({
        resolver: zodResolver(TradeAssetsPageSchema),
        mode: 'onChange',
        defaultValues: {
            tokenAmount: '0',
            usdAmount: '0',
            fromToken: undefined,
            toToken: null
        },
    });
    var _e = [
        form.watch(),
        form.watch('usdAmount'),
        form.watch('fromToken'),
        form.watch('toToken'),
    ], _formWatch = _e[0], _watchUsdAmount = _e[1], _watchFromToken = _e[2], _watchToToken = _e[3];
    // find user balance if tokenData
    var _assetBalance = useMemo(function () {
        if (!assetData || !tokenData)
            return undefined;
        return assetData.balances.find(function (token) {
            var _a;
            return token.contract_address.toLocaleLowerCase() ===
                ((_a = tokenData === null || tokenData === void 0 ? void 0 : tokenData.token) === null || _a === void 0 ? void 0 : _a.address.toLocaleLowerCase()) ||
                token.contract_address.toLocaleLowerCase() === tokenAddress.toLocaleLowerCase();
        });
    }, [assetData, tokenData]);
    // find to token user balance if any.
    var _toTokenBalance = useMemo(function () {
        if (!assetData || !_watchToToken)
            return undefined;
        return assetData.balances.find(function (token) {
            return token.contract_address.toLocaleLowerCase() ===
                (_watchToToken === null || _watchToToken === void 0 ? void 0 : _watchToToken.contract_address.toLocaleLowerCase());
        });
    }, [_watchToToken]);
    var handleOnSwitchTokenSides = function () {
        form.setValue('usdAmount', '0');
        form.setValue('tokenAmount', '0');
        if (_watchFromToken !== _watchToToken) {
            form.setValue('fromToken', _watchToToken);
        }
        else {
            form.setValue('fromToken', _watchFromToken);
        }
        console.log('setting to token 1', _assetBalance !== null && _assetBalance !== void 0 ? _assetBalance : _convertTokenData());
        _watchToToken !== _watchFromToken
            ? form.setValue('toToken', _watchFromToken)
            : form.setValue('toToken', _watchToToken);
        return;
    };
    // on confirm stage, set swap assets
    // to reach confirm stage, the form must be already valid
    useEffect(function () {
        if (_watchFromToken && _watchUsdAmount && _watchToToken) {
            setFromAssets([
                __assign(__assign({}, _watchFromToken), { amountToUseStr: _watchUsdAmount, amountToUse: Number(_watchUsdAmount) }),
            ]);
            setToAssets([_watchToToken]);
        }
        return function () { };
    }, [_watchFromToken, _watchUsdAmount, _watchToToken]);
    // on balance change, update it
    useEffect(function () {
        if (_assetBalance &&
            (_watchFromToken === null || _watchFromToken === void 0 ? void 0 : _watchFromToken.contract_address.toLocaleLowerCase()) ===
                (_assetBalance === null || _assetBalance === void 0 ? void 0 : _assetBalance.contract_address.toLocaleLowerCase()))
            form.setValue('fromToken', _assetBalance !== null && _assetBalance !== void 0 ? _assetBalance : _convertTokenData());
        if (_toTokenBalance &&
            (_watchToToken === null || _watchToToken === void 0 ? void 0 : _watchToToken.contract_address.toLocaleLowerCase()) ===
                (_toTokenBalance === null || _toTokenBalance === void 0 ? void 0 : _toTokenBalance.contract_address.toLocaleLowerCase()))
            console.log('setting to token 2', _assetBalance, _convertTokenData(), tokenData, _watchToToken, _toTokenBalance);
        form.setValue('toToken', _toTokenBalance);
        return;
    }, [_assetBalance, _toTokenBalance]);
    useEffect(function () {
        if (tokenAddress && tokenNetwork && tokenSide) {
            if (tokenSide === 'from' &&
                tokenData &&
                (_watchToToken === null || _watchToToken === void 0 ? void 0 : _watchToToken.contract_address.toLocaleLowerCase()) !== tokenAddress.toLocaleLowerCase()) {
                form.setValue('fromToken', _assetBalance !== null && _assetBalance !== void 0 ? _assetBalance : _convertTokenData());
                return;
            }
            if (tokenSide === 'to' &&
                tokenData &&
                (_watchFromToken === null || _watchFromToken === void 0 ? void 0 : _watchFromToken.contract_address.toLocaleLowerCase()) !== tokenAddress.toLocaleLowerCase()) {
                console.log('setting to token 3', _assetBalance !== null && _assetBalance !== void 0 ? _assetBalance : _convertTokenData());
                form.setValue('toToken', _assetBalance !== null && _assetBalance !== void 0 ? _assetBalance : _convertTokenData());
                return;
            }
        }
        return function () { };
    }, [tokenData, tokenAddress, tokenNetwork, tokenSide]);
    console.log('setting to token to token', _watchToToken, toAssets);
    // console.log('_onSwapResult', { quote, fromAssets, toAssets, ts: _watchTradeSide });
    // console.log('useTradeForm', {
    //   assetData,
    //   tokenData,
    //   _assetBalance,
    //   fromToken: _watchFromToken,
    //   toToken: _watchToToken,
    // });
    return {
        form: form,
        refetchOrderHistory: refetchOrderHistory,
        setMode: setMode,
        setKeyword: setKeyword,
        stage: stage,
        setStage: setStage,
        assetData: assetData,
        handleOnSwitchTokenSides: handleOnSwitchTokenSides,
    };
};
